import { Card, Row, Col, Typography, Spin, Alert } from "antd";
import { useState, useEffect } from "react";
import { adjacentCropService } from "../../services/api/adjacentCropService";
import type { AdjacentCropDashboardData } from "../../types/adjacent-crop";
import { AdjacentCropKPIs } from "./AdjacentCropKPIs";
import { SpeciesDistributionChart } from "./SpeciesDistributionChart";
import { VarietyBreakdownChart } from "./VarietyBreakdownChart";
import { SurfaceBySpeciesChart } from "./SurfaceBySpeciesChart";
import { StatusOverTimeChart } from "./StatusOverTimeChart";
import { TopProducersChart } from "./TopProducersChart";

const { Text } = Typography;

interface AdjacentCropStatsProps {
  selectedCountry?: number;
  countryName?: string;
}

export const AdjacentCropStats: React.FC<AdjacentCropStatsProps> = ({ 
  selectedCountry,
  countryName 
}) => {
  const [dashboardData, setDashboardData] = useState<AdjacentCropDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load dashboard data when filters change
  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const filters: any = {};

        if (selectedCountry) {
          filters.country_id = selectedCountry;
        }

        const data = await adjacentCropService.getDashboardData(filters);
        setDashboardData(data);
      } catch (err: any) {
        setError(err.message || "Error al cargar los datos del dashboard");
      } finally {
        setIsLoading(false);
      }
    };
    loadDashboardData();
  }, [selectedCountry]);

  return (
    <div>
      {/* Dashboard Content */}
      {isLoading ? (
        <Card>
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Cargando datos de cultivos adyacentes...</Text>
            </div>
          </div>
        </Card>
      ) : error ? (
        <Card>
          <Alert
            message="Error al cargar datos"
            description={error}
            type="error"
            showIcon
          />
        </Card>
      ) : dashboardData ? (
        <>
          {/* KPIs */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24}>
              <AdjacentCropKPIs
                data={dashboardData.kpis}
                countryName={countryName}
              />
            </Col>
          </Row>

          {/* Species Distribution and Status Over Time */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <SpeciesDistributionChart
                data={dashboardData.species_distribution}
              />
            </Col>
            <Col xs={24} lg={12}>
              <StatusOverTimeChart data={dashboardData.evolution_by_year} />
            </Col>
          </Row>

          {/* Variety Breakdown and Surface by Species */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <VarietyBreakdownChart data={dashboardData.variety_breakdown} />
            </Col>
            <Col xs={24} lg={12}>
              <SurfaceBySpeciesChart
                data={dashboardData.species_distribution}
              />
            </Col>
          </Row>

          {/* Top Producers */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24}>
              <TopProducersChart data={dashboardData.top_producers} />
            </Col>
          </Row>
        </>
      ) : (
        <Card>
          <Alert
            message="Sin datos"
            description="No hay datos disponibles para mostrar"
            type="info"
            showIcon
          />
        </Card>
      )}
    </div>
  );
};
